import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Grid, Icon, IconButton, Modal, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { analysisService, ISendBiometria } from "../../../../shared/services/api/analysis/analysisService";

interface ModalResendBiometryLinkProps {
	isOpen: boolean;
	onClose: () => void;
	analysisId: string;
	initialEmail?: string;
	initialPhone?: string;
}

const formValidationSchemaBiometria: yup.SchemaOf<ISendBiometria> = yup.object().shape({
	email: yup.string().required("Campo Obrigatório"),
	id: yup.string().notRequired(),
	phone: yup.string().required("Campo Obrigatório"),
});

export const ModalResendBiometryLink: React.FC<ModalResendBiometryLinkProps> = ({ isOpen, onClose, analysisId, initialEmail, initialPhone }) => {
	const toastHandler = useToast();
	const { formRef } = useVForm();
	const [isLoading, setIsLoading] = useState(false);
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));

	const normalizedInitialEmail = useMemo(() => (initialEmail ?? "").trim(), [initialEmail]);
	const normalizedInitialPhone = useMemo(() => (initialPhone ?? "").replace(/\D/g, ""), [initialPhone]);

	const modalStyle = useMemo(
		() => ({
			position: "relative" as const,
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: "min(860px, 92vw)",
			maxWidth: "70%",
			bgcolor: "background.paper",
			border: "1px solid #605e5e",
			boxShadow: 24,
			p: 2,
			borderRadius: "6px",
			display: "flex",
			flexDirection: "column" as const,
			backgroundColor: "#FFF",
		}),
		[],
	);

	useEffect(() => {
		if (!isOpen) return;
		setIsLoading(false);

		const data = {
			email: normalizedInitialEmail,
			phone: normalizedInitialPhone,
		};

		formRef.current?.setData(data);
		formRef.current?.setErrors({});

		// Garante que o setData rode após os campos montarem (refs/inputs do VForm)
		const timeoutId = window.setTimeout(() => {
			formRef.current?.setData(data);
		}, 0);
		return () => window.clearTimeout(timeoutId);
	}, [isOpen, normalizedInitialEmail, normalizedInitialPhone]);

	const handleSubmit = (dados: ISendBiometria) => {
		dados.phone = (dados.phone || "").replace(/[^0-9]/g, "");
		const defaultErrorMessage = "Não foi possível reenviar o link de biometria. Tente novamente mais tarde.";

		formValidationSchemaBiometria
			.validate(dados, { abortEarly: false })
			.then(async (dadosValidados) => {
				setIsLoading(true);
				const result = await analysisService.updateSendBiometria(analysisId, { ...dadosValidados }, false);
				setIsLoading(false);

				if (result instanceof Error) {
					const rawMessage = (result.message || "").trim();
					const shouldHideTechnicalMessage =
						!rawMessage ||
						/^network error$/i.test(rawMessage) ||
						/request failed with status code\s*\d+/i.test(rawMessage) ||
						/^request\s*400$/i.test(rawMessage);
					const errorMessage = shouldHideTechnicalMessage ? defaultErrorMessage : rawMessage;

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: (
							<ToastContent title="Erro" content={errorMessage} />
						),
					});
					return;
				}

				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: <ToastContent title="Sucesso" content="Link de biometria reenviado com sucesso!" />,
				});
				onClose();
			})
			.catch((errors: yup.ValidationError) => {
				setIsLoading(false);
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
	};

	return (
		<Modal
			open={isOpen}
			aria-labelledby="modal-resend-biometry-title"
			onClose={(_, reason) => {
				if (reason === "backdropClick") return;
				if (isLoading) return;
				onClose();
			}}
		>
			<Box sx={modalStyle}>
				<LoadingComp isopen={isLoading} />
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography id="modal-resend-biometry-title" variant="h5" color="primary" sx={{ fontWeight: 300 }}>
						Reenviar link de biometria
					</Typography>
					<IconButton aria-label="Fechar" onClick={onClose} size="small" disabled={isLoading}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<Box mt={2}>
					<Alert
						severity="warning"
						variant="standard"
						sx={{
							border: "none",
							borderRadius: 2,
							backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
							"& .MuiAlert-icon": {
								color: "warning.main",
								mt: "2px",
							},
							"& .MuiAlert-message": {
								py: 0,
							},
						}}
					>
						<Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.45 }}>
							O e-mail e o telefone informados devem ser obrigatoriamente do locatário. Caso seja verificado posteriormente que não são do
							locatário, a GARANTTI poderá cancelar a garantia e recusar o pagamento de eventual sinistro.
						</Typography>
					</Alert>
				</Box>

				<Box mt={2}>
					<VForm
						key={isOpen ? `biometria-${analysisId}-${normalizedInitialEmail}-${normalizedInitialPhone}` : "biometria-closed"}
						id="formBiometria"
						placeholder="Your placeholder"
						onPointerEnterCapture={() => {}}
						onPointerLeaveCapture={() => {}}
						ref={formRef}
						onSubmit={handleSubmit}
					>
						<Grid container item direction="column" padding={1} spacing={2}>
							<Grid container item direction="row" spacing={2}>
								<Grid item xs={12} md={8}>
									<VTextField fullWidth label="Email" name="email" disabled={isLoading} defaultValue={normalizedInitialEmail} />
								</Grid>
								<Grid item xs={12} md={4}>
									<VMaskTextField
										fullWidth
										label="Telefone"
										name="phone"
										disabled={isLoading}
										mask="phone"
										defaultValue={normalizedInitialPhone}
									/>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							container
							item
							sm={12}
							sx={{ border: "none", display: "flex", justifyContent: "center", mt: 1 }}
							display={"flex"}
							flexDirection={smDown ? "column" : "row"}
							padding={2}
							gap={2}
						>
							<Button
								color="primary"
								disableElevation
								variant={"outlined"}
								startIcon={<Icon>arrow_back</Icon>}
								onClick={onClose}
								disabled={isLoading}
							>
								Voltar
							</Button>
							<Button
								color="primary"
								disableElevation
								variant={"contained"}
								startIcon={<Icon> check</Icon>}
								onClick={() => formRef.current?.submitForm()}
								disabled={isLoading}
							>
								Reenviar
							</Button>
						</Grid>
					</VForm>
				</Box>
			</Box>
		</Modal>
	);
};
